import React, { useEffect, useState } from "react";
import { Save, Key } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const DEFAULT = {
  brand_name: "Larcraft", logo_text: "Larcraft", logo_subtitle: "atelier — el yapımı", logo_url: "",
  contact_email: "", phone: "", whatsapp: "", instagram_url: "", tiktok_url: "",
  address: "", working_hours: "", meta_title: "", meta_description: "",
  announcement_bar: "", free_shipping_threshold: 0, currency_symbol: "₺",
};

export default function AdminSettings() {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setForm({ ...DEFAULT, ...(r.data || {}) })); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put("/admin/settings", { ...form, free_shipping_threshold: Number(form.free_shipping_threshold) || 0 });
      toast.success("Ayarlar güncellendi");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const changePw = async (e) => {
    e.preventDefault(); setPwSaving(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Şifre değiştirildi");
      setPw({ current_password: "", new_password: "" });
    } catch (err) { toast.error(formatErr(err)); }
    finally { setPwSaving(false); }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="font-heading text-4xl">Site Ayarları</h1>
      <p className="text-larc-muted mt-1">Marka ve site bilgilerini düzenleyin.</p>

      <form onSubmit={save} className="mt-8 space-y-6">
        <Card title="Marka">
          <Grid>
            <F label="Marka Adı"><Input value={form.brand_name} onChange={(v) => set("brand_name", v)} testid="s-brand" /></F>
            <F label="Logo Metni"><Input value={form.logo_text} onChange={(v) => set("logo_text", v)} testid="s-logo-text" /></F>
            <F label="Logo Alt Yazı"><Input value={form.logo_subtitle} onChange={(v) => set("logo_subtitle", v)} /></F>
            <F label="Logo Görsel URL (opsiyonel)"><Input value={form.logo_url} onChange={(v) => set("logo_url", v)} /></F>
          </Grid>
        </Card>

        <Card title="İletişim">
          <Grid>
            <F label="E-posta"><Input type="email" value={form.contact_email} onChange={(v) => set("contact_email", v)} /></F>
            <F label="Telefon"><Input value={form.phone} onChange={(v) => set("phone", v)} /></F>
            <F label="WhatsApp (sadece rakam, +90 dahil)"><Input value={form.whatsapp} onChange={(v) => set("whatsapp", v)} /></F>
            <F label="Instagram URL"><Input value={form.instagram_url} onChange={(v) => set("instagram_url", v)} /></F>
            <F label="TikTok URL"><Input value={form.tiktok_url} onChange={(v) => set("tiktok_url", v)} /></F>
            <F label="Çalışma Saatleri"><Input value={form.working_hours} onChange={(v) => set("working_hours", v)} /></F>
            <F label="Adres" full><Input value={form.address} onChange={(v) => set("address", v)} /></F>
          </Grid>
        </Card>

        <Card title="SEO">
          <Grid>
            <F label="Meta Başlık" full><Input value={form.meta_title} onChange={(v) => set("meta_title", v)} /></F>
            <F label="Meta Açıklama" full><Textarea value={form.meta_description} onChange={(v) => set("meta_description", v)} /></F>
          </Grid>
        </Card>

        <Card title="Mağaza">
          <Grid>
            <F label="Duyuru Çubuğu Yazısı" full><Input value={form.announcement_bar} onChange={(v) => set("announcement_bar", v)} testid="s-ann" /></F>
            <F label="Ücretsiz Kargo Limiti"><Input type="number" value={form.free_shipping_threshold} onChange={(v) => set("free_shipping_threshold", v)} /></F>
            <F label="Para Birimi Sembolü"><Input value={form.currency_symbol} onChange={(v) => set("currency_symbol", v)} /></F>
          </Grid>
        </Card>

        <div className="flex justify-end">
          <button disabled={saving} className="inline-flex items-center gap-2 bg-larc-ink text-white rounded-full px-6 py-3 text-sm hover:bg-larc-gold transition-colors disabled:opacity-50" data-testid="settings-save">
            <Save size={16} /> {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </form>

      <form onSubmit={changePw} className="mt-10 bg-white border border-larc-border rounded-2xl p-6">
        <h3 className="font-heading text-2xl flex items-center gap-2"><Key size={18} /> Şifre Değiştir</h3>
        <Grid>
          <F label="Mevcut Şifre"><Input type="password" value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })} testid="pw-current" /></F>
          <F label="Yeni Şifre"><Input type="password" value={pw.new_password} onChange={(v) => setPw({ ...pw, new_password: v })} testid="pw-new" /></F>
        </Grid>
        <div className="mt-5 flex justify-end">
          <button disabled={pwSaving} className="inline-flex items-center gap-2 bg-larc-gold text-white rounded-full px-6 py-3 text-sm hover:bg-larc-gold-hover transition-colors disabled:opacity-50" data-testid="pw-save">
            {pwSaving ? "Kaydediliyor…" : "Şifreyi Güncelle"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-larc-border rounded-2xl p-6">
      <h3 className="font-heading text-2xl mb-5">{title}</h3>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function F({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-xs uppercase tracking-[0.15em] text-larc-muted">{label}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
function Input({ value, onChange, type = "text", testid }) {
  return <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-larc-gold" data-testid={testid} />;
}
function Textarea({ value, onChange }) {
  return <textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-larc-gold" />;
}
